'use client';

import { useState, useEffect, Suspense } from 'react';
import { useSearchParams, useRouter } from 'next/navigation';
import { useUser } from '@clerk/nextjs';
import posthog from 'posthog-js';

function WaiverFormContent({ siteName, onComplete, returnTo, prefilledData }) {
  const searchParams = useSearchParams();
  const router = useRouter();
  const { user: clerkUser } = useUser();
  const [householdMembers, setHouseholdMembers] = useState([]);
  const [signingForMemberId, setSigningForMemberId] = useState(null); // null = myself
  const [formData, setFormData] = useState({
    fullName: prefilledData?.fullName || '',
    dateOfBirth: prefilledData?.dateOfBirth || '',
    email: prefilledData?.email || '',
    signature: '',
    isMinor: false,
    parentGuardianName: '',
    parentGuardianSignature: '',
  });
  const [acceptWaiver, setAcceptWaiver] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  // Fetch household members when logged in
  useEffect(() => {
    if (clerkUser?.id) {
      fetch('/api/household-members', { cache: 'no-store' })
        .then((res) => (res.ok ? res.json() : { householdMembers: [] }))
        .then((data) => setHouseholdMembers(data.householdMembers || []))
        .catch(() => setHouseholdMembers([]));
    }
  }, [clerkUser?.id]);

  // When "Signing for" changes to a household member, prefill form
  useEffect(() => {
    if (signingForMemberId && householdMembers.length > 0) {
      const member = householdMembers.find((m) => m.id === signingForMemberId);
      if (member) {
        const dob = member.dateOfBirth ? (typeof member.dateOfBirth === 'string' ? member.dateOfBirth.slice(0, 10) : member.dateOfBirth) : '';
        setFormData((prev) => ({
          ...prev,
          fullName: member.fullName || '',
          dateOfBirth: dob,
          isMinor: !!member.isMinor,
          parentGuardianName: prev.parentGuardianName,
          parentGuardianSignature: prev.parentGuardianSignature,
        }));
      }
    }
  }, [signingForMemberId, householdMembers]);

  // Update form data when prefilledData changes
  useEffect(() => {
    if (prefilledData) {
      setFormData(prev => ({
        ...prev,
        fullName: prefilledData.fullName || prev.fullName,
        dateOfBirth: prefilledData.dateOfBirth || prev.dateOfBirth,
        email: prefilledData.email || prev.email,
      }));
    }
  }, [prefilledData]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setMessage(null);

    if (!acceptWaiver) {
      setError('You must accept the waiver to continue');
      return;
    }

    setIsSubmitting(true);

    try {
      // Link waiver to logged-in user's account when they sign (for themselves OR for a minor/child).
      // This ensures all waivers they sign appear under their account (supports multiple children).
      const userId = clerkUser?.id || null;

      if (!formData.dateOfBirth || !/^\d{4}-\d{2}-\d{2}$/.test(formData.dateOfBirth)) {
        setError('A valid date of birth is required');
        setIsSubmitting(false);
        return;
      }

      const response = await fetch('/api/waivers', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId,
          householdMemberId: signingForMemberId || undefined,
          fullName: formData.fullName,
          dateOfBirth: formData.dateOfBirth,
          email: formData.email,
          signature: formData.signature,
          isMinor: formData.isMinor,
          parentGuardianName: formData.isMinor ? formData.parentGuardianName : null,
          parentGuardianSignature: formData.isMinor ? formData.parentGuardianSignature : null,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to submit waiver');
      }

      // Track waiver signed event in PostHog
      posthog.capture('waiver_signed', {
        is_minor: formData.isMinor,
        has_user_id: !!userId,
        email: formData.email,
        source: returnTo ? 'checkout_flow' : (onComplete ? 'onboarding' : 'standalone'),
      });

      // Identify user if we have their info
      if (userId) {
        posthog.identify(userId, {
          email: formData.email,
          name: formData.fullName,
          waiver_signed: true,
        });
      }

      setMessage('Waiver submitted successfully!');
      
      // If onComplete callback is provided (e.g., from onboarding), call it
      if (onComplete) {
        setTimeout(() => {
          onComplete();
        }, 1000);
        return;
      }
      
      // Check if there's a return URL (e.g., from checkout flow)
      const returnUrl = returnTo || searchParams?.get('returnTo');
      const subscriptionTypeId = searchParams?.get('subscriptionTypeId');
      const classType = searchParams?.get('classType');
      
      if (returnUrl) {
        // Redirect after a short delay to show success message
        setTimeout(() => {
          if (subscriptionTypeId) {
            // Build URL with subscriptionTypeId and optionally classType
            const url = new URL(returnUrl, window.location.origin);
            url.searchParams.set('subscriptionTypeId', subscriptionTypeId);
            if (classType) {
              url.searchParams.set('classType', classType);
            }
            // Use router.push for same-tab navigation
            router.push(url.pathname + url.search);
          } else {
            router.push(returnUrl);
          }
        }, 1500);
      } else {
        // Default redirect: signed-in users go to dashboard; guests go to sign-up (they don't have an account yet)
        setTimeout(() => {
          if (clerkUser) {
            router.push('/dashboard/subscriptions');
          } else {
            router.push('/sign-up');
          }
        }, 1500);
      }
    } catch (err) {
      setError(err.message || 'An error occurred while submitting the waiver');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6 md:p-8">
      {message && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <p className="font-semibold">Waiver submitted successfully!</p>
          <p className="text-sm text-gray-600 mt-1">
            You can now attend classes. Thank you for completing the waiver.
          </p>
        </div>
      )}

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="font-semibold text-red-800">Error</p>
          <p className="text-sm text-red-600 mt-1">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {clerkUser && householdMembers.length > 0 && (
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Signing for</label>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setSigningForMemberId(null)}
                className={`rounded-lg px-3 py-1.5 text-sm font-medium ${
                  !signingForMemberId ? 'bg-[#D4AF37] text-gray-900' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Myself
              </button>
              {householdMembers.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => setSigningForMemberId(m.id)}
                  className={`rounded-lg px-3 py-1.5 text-sm font-medium ${
                    signingForMemberId === m.id ? 'bg-[#D4AF37] text-gray-900' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {m.fullName}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Participant Information */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">
            Participant Information
          </h3>

          <div>
            <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-1">
              Full Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="fullName"
              name="fullName"
              required
              value={formData.fullName}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label htmlFor="dateOfBirth" className="block text-sm font-medium text-gray-700 mb-1">
              Date of Birth <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              id="dateOfBirth"
              name="dateOfBirth"
              required
              value={formData.dateOfBirth}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              required
              value={formData.email}
              onChange={handleChange}
              disabled={!!prefilledData?.email}
              className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                prefilledData?.email ? 'bg-gray-50 text-gray-600 cursor-not-allowed' : ''
              }`}
            />
            {prefilledData?.email && (
              <p className="text-xs text-gray-500 mt-1">Email from your account</p>
            )}
          </div>

          <div>
            <label htmlFor="signature" className="block text-sm font-medium text-gray-700 mb-1">
              Signature (Type your full name) <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="signature"
              name="signature"
              required
              value={formData.signature}
              onChange={handleChange}
              placeholder="Type your full name as your signature"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="isMinor"
              name="isMinor"
              checked={formData.isMinor}
              onChange={handleChange}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="isMinor" className="ml-2 block text-sm text-gray-700">
              I am under 18 years of age
            </label>
          </div>

          {formData.isMinor && (
            <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg space-y-4">
              <h4 className="font-semibold text-gray-900">Parent/Guardian Information</h4>
              
              <div>
                <label htmlFor="parentGuardianName" className="block text-sm font-medium text-gray-700 mb-1">
                  Parent/Guardian Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="parentGuardianName"
                  name="parentGuardianName"
                  required={formData.isMinor}
                  value={formData.parentGuardianName}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label htmlFor="parentGuardianSignature" className="block text-sm font-medium text-gray-700 mb-1">
                  Parent/Guardian Signature (Type full name) <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="parentGuardianSignature"
                  name="parentGuardianSignature"
                  required={formData.isMinor}
                  value={formData.parentGuardianSignature}
                  onChange={handleChange}
                  placeholder="Type parent/guardian full name as signature"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          )}
        </div>

        {/* Acceptance */}
        <div className="border-t pt-6">
          <div className="flex items-start">
            <input
              type="checkbox"
              id="acceptWaiver"
              checked={acceptWaiver}
              onChange={(e) => setAcceptWaiver(e.target.checked)}
              required
              className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="acceptWaiver" className="ml-2 block text-sm text-gray-700">
              By checking this box and submitting this form electronically, I agree that this action constitutes my legal signature, equivalent to a handwritten signature under California law. I acknowledge that I have read and understood this waiver and release. I understand that by agreeing, I am giving up certain legal rights, including the right to sue.
            </label>
          </div>
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-gray-900 text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-gray-800 transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? 'Submitting...' : 'Submit Waiver'}
        </button>
      </form>
    </div>
  );
}

export default function WaiverForm({ siteName, onComplete, returnTo, prefilledData }) {
  return (
    <Suspense fallback={
      <div className="bg-white rounded-lg shadow-md p-6 md:p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
        </div>
      </div>
    }>
      <WaiverFormContent siteName={siteName} onComplete={onComplete} returnTo={returnTo} prefilledData={prefilledData} />
    </Suspense>
  );
}